import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { saveToken, saveUserInfo } from '../utils/auth';
import { config } from '../config';

/**
 * OAuth 回调处理组件
 * 处理 Casdoor 授权后的回调，用 code 换取 token
 */
export const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (!code) {
      setError('缺少 code 参数');
      return;
    }

    // 防止重复处理
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const exchangeToken = async () => {
      try {
        // 调用后端接口，用 code 换取 access_token
        const result = await fetch(
          `${config.API_BASE}${config.TOKEN_URL}?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state || '')}`
        );

        const response = await result.json();

        if (response.code === 200) {
          const { access_token } = response.data;

          // 保存 token 到 localStorage
          saveToken(access_token);

          // 获取用户信息
          try {
            const userInfoResult = await fetch(`${config.API_BASE}${config.USER_URL}`, {
              headers: {
                'access_token': access_token,
              },
            });
            const userInfoResponse = await userInfoResult.json();
            if (userInfoResponse.code === 200) {
              saveUserInfo(userInfoResponse.data);
            }
          } catch (e) {
            console.warn('获取用户信息失败:', e);
          }

          // 跳转首页
          navigate('/');
        } else {
          setError(response.message || '登录失败，请重试');
        }
      } catch (err) {
        console.error('Token exchange error:', err);
        setError(err instanceof Error ? err.message : '登录失败，请重试');
      }
    };

    exchangeToken();
  }, [searchParams, navigate]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      {error ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <h1 style={{ color: '#dc2626', marginBottom: '16px' }}>登录失败</h1>
          <p style={{ color: '#666', marginBottom: '24px' }}>{error}</p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
            }}
          >
            返回首页
          </button>
        </div>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 24px',
          }} />
          <h2 style={{ marginBottom: '8px' }}>登录中...</h2>
          <p style={{ color: '#666' }}>正在处理授权，请稍候</p>
        </div>
      )}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
